import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useApiGet } from '../../utils/httpClientTs';
import { getTrainingDetail } from '../../endpoints/training';
import ReactPlayer from 'react-player';
import PageHeader from '../../Components/PageHeader';
import QuizModal from './QuizModal';
import { Training } from '../../types/training';
import trainingLogo from '../../assets/DefaultIcon.png';
import AlertItem from '../../Components/AlertItem';

const TrainingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quizOpen, setQuizOpen] = useState(false);

  const {
    data: trainingResponse,
    isLoading,
    error,
    refetch,
  } = useApiGet(['training-detail', id], () => getTrainingDetail({ id: id! }), {
    enabled: !!id,
  });

  // Handle both object and response formats
  const training =
    trainingResponse && 'data' in trainingResponse
      ? trainingResponse.data
      : (trainingResponse as unknown as Training);

  const handleStartQuiz = () => {
    if (training?.questions && training.questions.length > 0) {
      setQuizOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !training) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="Error loading training details"
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Back and Edit Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/training')}
        >
          Back to Training
        </Button>
        {training.can_manage && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/training/${training.id}/edit`)}
          >
            Edit
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Video Section - Full Width */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ mb: 1 }}>
                <PageHeader
                  title={training.name}
                  subtitle={training.description}
                  rightDom={null}
                  leftDom={null}
                />
              </Box>

              {/* Video Player */}
              <Box sx={{ mb: 2 }}>
                <ReactPlayer
                  className="react-player"
                  width="100%"
                  height="70vh"
                  url={training.video_url}
                  playsinline={true}
                  controls={true}
                  config={{
                    file: {
                      attributes: {
                        controlsList: 'nodownload',
                      },
                    },
                  }}
                />
              </Box>

              {/* Training Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Training Summary
                </Typography>
                <Typography variant="body1" paragraph>
                  {training.summary}
                </Typography>
              </Box>

              {/* Transcript */}
              {training.transcript && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Video Transcript
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {training.transcript}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Categories and Topics */}
              {(training.categories?.length || training.topics?.length) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Training Details
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {training.categories?.map((cat, index) => (
                      <Chip
                        key={`cat-${typeof cat === 'object' ? (cat as { id: string }).id : cat}-${index}`}
                        label={
                          typeof cat === 'object' && cat && 'name' in cat
                            ? (cat as { id: string; name: string }).name
                            : String(cat)
                        }
                        color="primary"
                        variant="outlined"
                        icon={<SchoolIcon />}
                      />
                    ))}
                    {training.topics?.map((topic, index) => (
                      <Chip
                        key={`topic-${typeof topic === 'object' ? (topic as { id: string }).id : topic}-${index}`}
                        label={
                          typeof topic === 'object' && topic && 'name' in topic
                            ? (topic as { id: string; name: string }).name
                            : String(topic)
                        }
                        color="secondary"
                        variant="outlined"
                        icon={<DescriptionIcon />}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {training.questions && training.questions.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<QuizIcon />}
                    onClick={handleStartQuiz}
                    sx={{
                      backgroundColor: '#0072CE',
                      '&:hover': {
                        backgroundColor: '#005a9e',
                      },
                    }}
                  >
                    Take Quiz ({training.questions.length} questions)
                  </Button>
                )}

                {/* Hide Mark as Complete button for now */}
                {/* {!training.user_completed && (
                  <Button
                    variant="outlined"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleCompleteTraining}
                    color="success"
                    disabled={isCompleting}
                  >
                    {isCompleting ? 'Completing...' : 'Mark as Complete'}
                  </Button>
                )} */}

                {training.user_completed && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Completed"
                    color="success"
                    variant="filled"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar - Moved below video */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Related Trainings
              </Typography>
              {training.related_trainings &&
              training.related_trainings.length > 0 ? (
                <Grid container spacing={2}>
                  {training.related_trainings.map((relatedTraining) => (
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      md={3}
                      lg={2}
                      key={relatedTraining.id}
                    >
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 2,
                          },
                        }}
                        onClick={() =>
                          navigate(`/training/${relatedTraining.id}`)
                        }
                      >
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          <Box
                            component="img"
                            src={relatedTraining.icon_image_url || trainingLogo}
                            alt={relatedTraining.name}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 120,
                              objectFit: 'contain',
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {relatedTraining.name}
                          </Typography>
                          {relatedTraining.user_completed && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Completed"
                              size="small"
                              color="success"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No related trainings available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quiz Modal */}
      {training.questions && training.questions.length > 0 && (
        <QuizModal
          quizOpen={quizOpen}
          setOpen={setQuizOpen}
          quizQuestions={training.questions}
          trainingId={training.id}
          refetchParent={refetch}
        />
      )}
    </Box>
  );
};

export default TrainingDetail;
